import React, { useState, useEffect } from 'react';
import useDebounce from '../../hooks/useDebounce';
import { Plus, Edit2, Trash2, ChevronLeft, ChevronRight, Calendar, Paperclip, FileText, X } from 'lucide-react';
import Dropdown from '../../components/Dropdown';
import DateFilter from '../../components/DateFilter';
import { isDateInRange, formatAppDate, formatAppDateOnly } from '../../utils/dateUtils';
import toast from 'react-hot-toast';
import api, { BASE_URL } from '../../utils/api';
import PrinterLoader from '../../components/PrinterLoader';

const MasterTable = ({ title, mockItems }) => {
  const [searchTerm, setSearchTerm] = useState('');
  const [dateFilter, setDateFilter] = useState('all');
  const [customStartDate, setCustomStartDate] = useState('');
  const [customEndDate, setCustomEndDate] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(10);
  const [totalPages, setTotalPages] = useState(1);
  const [totalItems, setTotalItems] = useState(0);

  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);

  const debouncedSearchTerm = useDebounce(searchTerm, 500);

  const [showModal, setShowModal] = useState(false);
  const [deleteModalOpen, setDeleteModalOpen] = useState(false);
  const [itemToDelete, setItemToDelete] = useState(null);
  const [editId, setEditId] = useState(null);
  const [formData, setFormData] = useState({ name: '', status: 'Active', brochure: '' });

  // Map title to backend route
    const getSingularName = () => {
    if (title.includes('Delivery Status')) return 'Delivery Status';
    if (title.includes('Interest Types')) return 'Interest Type';
    if (title.includes('Statuses')) return 'Status';
    if (title.includes('Machines')) return 'Machine';
    if (title.includes('Lead Sources')) return 'Lead Source';
    if (title.includes('Email Templates')) return 'Email Template';
    if (title.includes('Customer Types')) return 'Customer Type';
    return 'Record';
  };

  const getRoute = () => {
    switch(title) {
      case 'Manage Delivery Statuses': return '/masters/delivery-statuses';
      case 'Manage Interest Types': return '/masters/lead-types';
      case 'Manage Statuses': return '/masters/status';
      case 'Manage Machines': return '/masters/machines';
      case 'Manage Lead Sources': return '/masters/lead-sources';
      case 'Manage Email Templates': return '/masters/email-templates';
      case 'Manage Customer Types': return '/masters/customer-types';
      default: return '';
    }
  };

  const confirmDelete = (id) => {
    setItemToDelete(id);
    setDeleteModalOpen(true);
  };

  const handleDelete = async () => {
    if (itemToDelete) {
      try {
        await api.delete(`${getRoute()}/${itemToDelete}`);
        toast.success('Record deleted successfully');
        setDeleteModalOpen(false);
        setItemToDelete(null);
        fetchItems();
      } catch (err) {
        toast.error(err.response?.data?.message || 'Failed to delete record');
      }
    }
  };

  const fetchItems = async () => {
    const route = getRoute();
    if (!route) {
      if (mockItems) setItems(mockItems);
      setLoading(false);
      return;
    }

    try {
      setLoading(true);
      
      const queryParams = new URLSearchParams({
        page: currentPage,
        limit: itemsPerPage
      });
      
      if (debouncedSearchTerm) queryParams.append('search', debouncedSearchTerm);
      
      const res = await api.get(`${route}?${queryParams.toString()}`);
      
      if (res.data.data) {
        setItems(res.data.data);
        setTotalPages(res.data.totalPages || 1);
        setTotalItems(res.data.total || 0);
      } else {
        setItems(res.data);
        setTotalPages(1);
        setTotalItems(res.data.length);
      }
    } catch (err) {
      toast.error('Failed to load records');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchItems();
  }, [title, currentPage, itemsPerPage, debouncedSearchTerm]);

  const handleOpenModal = (item = null) => {
    if (item) {
      setEditId(item.id || item._id);
      setFormData({ 
        name: item.name, 
        status: item.status, 
        brochure: item.brochure || '',
        subject: item.subject || '',
        body: item.body || '',
        order: item.order || 0
      });
    } else {
      setEditId(null);
      setFormData({ name: '', status: 'Active', brochure: '', subject: '', body: '', order: 0 });
    }
    setShowModal(true);
  };

  const handleSave = async () => {
    const route = getRoute();
    if (!route) return;

    try {
      const payload = {
        name: formData.name,
        status: formData.status,
        order: parseInt(formData.order) || 0
      };

      if (title === 'Manage Machines') {
        payload.brochure = formData.brochure || null;
      }
      
      if (title === 'Manage Email Templates') {
        payload.subject = formData.subject || '';
        payload.body = formData.body || '';
      }

      if (editId) {
        await api.put(`${route}/${editId}`, payload);
        toast.success(`${title} record updated successfully!`);
      } else {
        await api.post(route, payload);
        toast.success(`${title} record added successfully!`);
      }
      setShowModal(false);
      fetchItems();
    } catch (err) {
      const status = err.response?.status || 'Unknown';
      const msg = err.response?.data?.message || (typeof err.response?.data === 'string' ? err.response.data.substring(0, 50) : 'Server error');
      toast.error(`Error ${status}: ${msg}`);
      console.error("Save error details:", err.response);
    }
  };



  const paginatedItems = items.filter(item => {
    const matchesDate = isDateInRange(item.createdAt, dateFilter, customStartDate, customEndDate);
    return matchesDate;
  });

  const handleViewBrochure = (e, brochureString) => {
    e.preventDefault();
    if (!brochureString) return;
    
    let fullUrl = brochureString;
    if (brochureString.startsWith('/uploads')) {
      fullUrl = `${BASE_URL}${brochureString}`;
    }

    if (fullUrl.startsWith('http') || fullUrl.startsWith('data:')) {
      window.open(fullUrl, '_blank');
      return;
    }
  };

  const handlePageChange = (page) => {
    if (page >= 1 && page <= totalPages) {
      setCurrentPage(page);
    }
  };

  return (
    <div>
      {loading && <PrinterLoader />}
      <div className="flex justify-between items-center mb-6">
        <div>
          <h2 className="text-2xl font-bold">{title}</h2>
          <p className="text-muted mt-2">Manage the master data records for {title.toLowerCase()}.</p>
        </div>
        <button className="btn btn-primary gap-2" onClick={() => handleOpenModal()}>
          <Plus size={18} />
          Add New
        </button>
      </div>

      <div className="card">
        <div className="flex flex-col gap-4 mb-6">
          <div className="flex justify-between items-center gap-4 flex-wrap">
             <input 
               type="text" 
               placeholder={`Search ${
                  title.includes('Status') ? 'statuses' :
                  title.includes('Interest') ? 'interest types' :
                  title.includes('Source') ? 'lead sources' :
                  title.includes('Customer') ? 'customer types' :
                  title.includes('Machine') ? 'machines' :
                  title.includes('Template') ? 'email templates' :
                  'records'
               }...`}
               className="input-field" 
               style={{ flex: '1', minWidth: '250px', maxWidth: '320px' }}
               value={searchTerm}
               onChange={(e) => {
                 setSearchTerm(e.target.value);
                 setCurrentPage(1);
               }} 
             />
             <div className="flex gap-4 items-center flex-wrap">
               {title !== 'Manage Email Templates' && (
                 <>
                   <div className="flex items-center gap-2">
                     <DateFilter 
                       value={dateFilter}
                       onChange={(val) => {
                         setDateFilter(val);
                         setCurrentPage(1);
                       }}
                     />
                   </div>
                   <button className="btn" style={{ border: '1.5px solid var(--color-border)', backgroundColor: 'white' }}>Export</button>
                 </>
               )}
             </div>
          </div>
          
          {/* Custom Date Range Picker */}
          {dateFilter === 'custom' && (
            <div className="flex justify-end gap-4 fade-in">
               <div className="flex items-center gap-2">
                 <span className="text-sm font-semibold text-muted">From:</span>
                 <input 
                   type="date" 
                   className="input-field" 
                   value={customStartDate}
                   onChange={e => {
                     setCustomStartDate(e.target.value);
                     setCurrentPage(1);
                   }}
                 />
               </div>
               <div className="flex items-center gap-2">
                 <span className="text-sm font-semibold text-muted">To:</span>
                 <input 
                   type="date" 
                   className="input-field" 
                   value={customEndDate}
                   onChange={e => {
                     setCustomEndDate(e.target.value);
                     setCurrentPage(1);
                   }}
                 />
               </div>
            </div>
          )}
        </div>

        <div style={{ overflowX: 'auto' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse', textAlign: 'left' }}>
            <thead>
              <tr style={{ borderBottom: '1px solid var(--color-border)', color: 'var(--color-text-muted)' }}>
                <th style={{ padding: '1rem' }}>Sr. No.</th>
                <th style={{ padding: '1rem' }}>Name</th>
                <th style={{ padding: '1rem' }}>Sequence</th>
                {title === 'Manage Machines' && <th style={{ padding: '1rem' }}>Brochure</th>}
                {title === 'Manage Email Templates' && <th style={{ padding: '1rem' }}>Subject</th>}
                <th style={{ padding: '1rem' }}>Created Date</th>
                <th style={{ padding: '1rem' }}>Status</th>
                <th style={{ padding: '1rem', textAlign: 'center' }}>Actions</th>
              </tr>
            </thead>
            <tbody>
              {paginatedItems.length > 0 ? (
                paginatedItems.map((item, index) => (
                  <tr key={item.id || item._id} style={{ borderBottom: '1px solid var(--color-border)' }}>
                    <td style={{ padding: '1rem', color: 'var(--color-text-muted)' }}>
                      {(currentPage - 1) * itemsPerPage + index + 1}
                    </td>
                    <td style={{ padding: '1rem', fontWeight: '500' }}>{item.name}</td>
                    <td style={{ padding: '1rem', color: 'var(--color-text-muted)' }}>{item.order || 0}</td>
                    {title === 'Manage Machines' && (
                      <td style={{ padding: '1rem' }}>
                        {item.brochure ? (
                          <a href="#" onClick={(e) => handleViewBrochure(e, item.brochure)} style={{ color: 'var(--color-primary)', display: 'flex', alignItems: 'center', gap: '0.25rem', fontSize: '0.875rem', fontWeight: '500', textDecoration: 'none' }}>
                            <Paperclip size={14} /> View
                          </a>
                        ) : (
                          <span style={{ color: 'var(--color-text-muted)', fontSize: '0.75rem' }}>No brochure</span>
                        )}
                      </td>
                    )}
                    {title === 'Manage Email Templates' && (
                      <td style={{ padding: '1rem', color: 'var(--color-text-muted)' }}>{item.subject}</td>
                    )}
                    <td style={{ padding: '1rem', color: 'var(--color-text-muted)', fontSize: '0.875rem' }}>
                      {formatAppDate(item.createdAt)}
                    </td>
                    <td style={{ padding: '1rem' }}>
                      <span style={{ 
                        padding: '0.25rem 0.6rem', 
                        borderRadius: '9999px', 
                        backgroundColor: item.status === 'Active' ? '#dcfce7' : '#f1f5f9',
                        color: item.status === 'Active' ? '#166534' : '#475569',
                        fontSize: '0.75rem',
                        fontWeight: '600'
                      }}>
                        {item.status}
                      </span>
                    </td>
                    <td style={{ padding: '1rem', textAlign: 'center' }}>
                      <div className="flex gap-2 justify-center">
                        <button className="btn text-muted" style={{ padding: '0.3rem' }} title="Edit" onClick={() => handleOpenModal(item)}>
                          <Edit2 size={16} />
                        </button>
                        <button className="btn text-danger" style={{ padding: '0.3rem' }} title="Delete" onClick={() => confirmDelete(item.id)}>
                          <Trash2 size={16} color="var(--color-danger)" />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))
              ) : (
                <tr>
                  <td colSpan="4" style={{ padding: '2rem', textAlign: 'center', color: 'var(--color-text-muted)' }}>
                    No records found matching your search.
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
        
        {/* Pagination Controls */}
        {totalItems > 0 && (
          <div className="flex justify-between items-center mt-6 pt-4 border-t" style={{ borderColor: 'var(--color-border)' }}>
            <div className="flex items-center gap-4 text-sm text-muted">
              <div>
                Showing <span className="font-semibold" style={{ color: 'var(--color-text-main)' }}>{(currentPage - 1) * itemsPerPage + 1}</span> to <span className="font-semibold" style={{ color: 'var(--color-text-main)' }}>{Math.min(currentPage * itemsPerPage, totalItems)}</span> of <span className="font-semibold" style={{ color: 'var(--color-text-main)' }}>{totalItems}</span> entries
              </div>
              <div className="flex items-center gap-2">
                <span>Show:</span>
                <Dropdown 
                  style={{ width: '80px', height: '32px' }}
                  value={itemsPerPage}
                  onChange={(val) => {
                    setItemsPerPage(Number(val));
                    setCurrentPage(1);
                  }}
                  options={[
                    { value: 5, label: '5' },
                    { value: 10, label: '10' },
                    { value: 20, label: '20' },
                    { value: 50, label: '50' }
                  ]}
                />
              </div>
            </div>
            <div className="flex gap-2">
              <button 
                className="btn" 
                style={{ padding: '0.5rem', border: '1px solid var(--color-border)', backgroundColor: currentPage === 1 ? '#f8fafc' : 'white', opacity: currentPage === 1 ? 0.5 : 1 }}
                onClick={() => handlePageChange(currentPage - 1)}
                disabled={currentPage === 1}
              >
                <ChevronLeft size={18} />
              </button>
              
              <div className="flex items-center gap-1 mx-2">
                {[...Array(totalPages)].map((_, i) => (
                  <button 
                    key={i} 
                    className="btn"
                    style={{ 
                      padding: '0.25rem 0.75rem', 
                      backgroundColor: currentPage === i + 1 ? 'var(--color-primary)' : 'transparent',
                      color: currentPage === i + 1 ? 'white' : 'var(--color-text-main)',
                      fontWeight: currentPage === i + 1 ? '600' : '400'
                    }}
                    onClick={() => handlePageChange(i + 1)}
                  >
                    {i + 1}
                  </button>
                ))}
              </div>

              <button 
                className="btn" 
                style={{ padding: '0.5rem', border: '1px solid var(--color-border)', backgroundColor: currentPage === totalPages ? '#f8fafc' : 'white', opacity: currentPage === totalPages ? 0.5 : 1 }}
                onClick={() => handlePageChange(currentPage + 1)}
                disabled={currentPage === totalPages}
              >
                <ChevronRight size={18} />
              </button>
            </div>
          </div>
        )}
      </div>

      {/* Modal */}
      {showModal && (
        <div className="modal-overlay" style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          backgroundColor: 'rgba(15, 23, 42, 0.6)',
          backdropFilter: 'blur(4px)',
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          zIndex: 100
        }}>
          <div className="modal-content" style={{
            backgroundColor: 'var(--color-bg-card)',
            padding: '2rem',
            borderRadius: 'var(--border-radius-lg)',
            width: '100%',
            maxWidth: title === 'Manage Email Templates' ? '800px' : '500px',
            boxShadow: 'var(--shadow-xl)'
          }}>
            <div className="flex justify-between items-center mb-6">
              <h3 className="text-xl font-bold">
                {editId ? 'Edit' : 'Add'} {
                  title.includes('Status') ? 'Status' :
                  title.includes('Interest') ? 'Interest Type' :
                  title.includes('Source') ? 'Lead Source' :
                  title.includes('Customer') ? 'Customer Type' :
                  title.includes('Machine') ? 'Machine' :
                  title.includes('Template') ? 'Email Template' :
                  'Record'
                }
              </h3>
              <button 
                type="button" 
                className="btn text-muted" 
                style={{ background: 'none', border: 'none', padding: '0.5rem', cursor: 'pointer' }} 
                onClick={() => {
                  setShowModal(false);
                  setEditId(null);
                  setFormData({ name: '', status: 'Active', subject: '', body: '', brochure: null, order: 0 });
                }}
              >
                <X size={24} />
              </button>
            </div>
            
            <div className="flex flex-col gap-4">
              <div>
                <label className="block text-sm font-semibold mb-2">Name</label>
                <input 
                  type="text" 
                  className="input-field w-full"
                  style={{ width: '100%' }}
                  value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  placeholder={title === 'Manage Email Templates' ? "e.g. Welcome Email" : "Enter name"}
                />
              </div>

              <div>
                <label className="block text-sm font-semibold mb-2">Sort Order</label>
                <input 
                  type="number" 
                  className="input-field w-full"
                  style={{ width: '100%' }}
                  value={formData.order}
                  onChange={(e) => setFormData({ ...formData, order: e.target.value })}
                  placeholder="e.g. 1"
                  min="0"
                />
              </div>

              {title === 'Manage Email Templates' && (
                <>
                  <div>
                    <label className="block text-sm font-semibold mb-2">Email Subject</label>
                    <input 
                      type="text" 
                      className="input-field w-full"
                      style={{ width: '100%' }}
                      value={formData.subject || ''}
                      onChange={(e) => setFormData({ ...formData, subject: e.target.value })}
                      placeholder="Enter email subject"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-semibold mb-2">Email Body</label>
                    <div style={{ backgroundColor: 'white', border: '1px solid var(--color-border)', borderRadius: '4px', overflow: 'hidden' }}>
                      <div className="flex gap-2 p-2 border-b" style={{ borderColor: 'var(--color-border)', backgroundColor: '#f8fafc' }}>
                        <button type="button" className="btn" style={{ padding: '0.2rem 0.5rem', fontWeight: 'bold' }}>B</button>
                        <button type="button" className="btn" style={{ padding: '0.2rem 0.5rem', fontStyle: 'italic' }}>I</button>
                        <button type="button" className="btn" style={{ padding: '0.2rem 0.5rem', textDecoration: 'underline' }}>U</button>
                        <div style={{ width: '1px', backgroundColor: 'var(--color-border)', margin: '0 4px' }}></div>
                        <button type="button" className="btn" style={{ padding: '0.2rem 0.5rem' }}>Link</button>
                      </div>
                      <textarea 
                        className="input-field w-full"
                        style={{ width: '100%', minHeight: '150px', border: 'none', resize: 'vertical' }}
                        value={formData.body || ''}
                        onChange={(e) => setFormData({ ...formData, body: e.target.value })}
                        placeholder="Enter email content (e.g. Hi {lead_name}...)"
                      ></textarea>
                    </div>
                  </div>
                </>
              )}

              {title === 'Manage Machines' && (
                <div>
                  <label className="block text-sm font-semibold mb-2">Upload Brochure (Optional)</label>
                  <input 
                    type="file" 
                    accept=".pdf,.doc,.docx"
                    className="input-field w-full"
                    style={{ width: '100%', padding: '0.4rem' }}
                    onChange={(e) => {
                      const file = e.target.files[0];
                      if (file) {
                        const reader = new FileReader();
                        reader.onloadend = () => {
                          setFormData({ ...formData, brochure: reader.result });
                        };
                        reader.readAsDataURL(file);
                      }
                    }}
                  />
                  {formData.brochure && formData.brochure.length > 0 && (
                    <div className="text-xs text-primary mt-2 flex items-center gap-1">
                      <Paperclip size={12} /> <a href="#" onClick={(e) => handleViewBrochure(e, formData.brochure)} style={{ textDecoration: 'underline' }}>View Currently Uploaded Brochure</a>
                    </div>
                  )}
                </div>
              )}
              
              <div>
                <label className="block text-sm font-semibold mb-2">Status</label>
                <Dropdown 
                  value={formData.status}
                  onChange={(val) => setFormData({ ...formData, status: val })}
                  options={[
                    { 
                      value: 'Active', 
                      label: 'Active',
                      render: () => (
                        <div className="flex items-center gap-2">
                          <span style={{ width: '8px', height: '8px', borderRadius: '50%', backgroundColor: '#10b981' }}></span>
                          Active
                        </div>
                      )
                    },
                    { 
                      value: 'Inactive', 
                      label: 'Inactive',
                      render: () => (
                        <div className="flex items-center gap-2">
                          <span style={{ width: '8px', height: '8px', borderRadius: '50%', backgroundColor: '#94a3b8' }}></span>
                          Inactive
                        </div>
                      )
                    }
                  ]}
                />
              </div>
            </div>

            <div className="flex justify-end gap-3 mt-8">
              <button 
                className="btn text-muted" 
                onClick={() => setShowModal(false)}
                style={{ backgroundColor: 'var(--color-bg-main)' }}
              >
                Cancel
              </button>
              <button 
                className="btn btn-primary"
                onClick={handleSave}
                disabled={!formData.name}
              >
                Save
              </button>
            </div>
          </div>
        </div>
      )}
      {deleteModalOpen && (
        <div style={{ position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, backgroundColor: 'rgba(15, 23, 42, 0.6)', backdropFilter: 'blur(4px)', display: 'flex', justifyContent: 'center', alignItems: 'center', zIndex: 9999, padding: '1rem' }} onClick={() => setDeleteModalOpen(false)}>
          <div onClick={e => e.stopPropagation()} className="slide-up" style={{ backgroundColor: 'white', borderRadius: '12px', width: '100%', maxWidth: '400px', padding: '2rem', boxShadow: '0 20px 25px -5px rgba(0, 0, 0, 0.1)', textAlign: 'center' }}>
            <div style={{ width: '4rem', height: '4rem', borderRadius: '50%', backgroundColor: '#fee2e2', display: 'flex', alignItems: 'center', justifyContent: 'center', margin: '0 auto 1.5rem auto' }}>
              <Trash2 size={32} color="var(--color-danger)" />
            </div>
            <h3 className="text-xl font-bold mb-3 text-main">Delete {getSingularName()}</h3>
            <p className="text-muted mb-8 text-sm">Are you sure you want to delete this {getSingularName().toLowerCase()}? This action cannot be undone.</p>
            <div className="flex gap-4 justify-center">
              <button className="btn flex-1" onClick={() => setDeleteModalOpen(false)} style={{ border: '1px solid var(--color-border)', backgroundColor: 'white' }}>Cancel</button>
              <button className="btn btn-primary flex-1" onClick={handleDelete} style={{ backgroundColor: 'var(--color-danger)', borderColor: 'var(--color-danger)' }}>Delete</button>
            </div>
          </div>
        </div>
      )}
      
    </div>
  );
};

export default MasterTable;